import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Spin,
  Result,
  Space,
  Alert,
  message,
  Modal,
  Divider,
} from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import * as eventsService from '../../../services/eventsService';
import { getFileUrl } from '../../../utils/urlUtils';
import styles from './EventRegistration.module.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const EventRegistration = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editCode = searchParams.get('code');
  const isEditMode = !!editCode;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [recoveryExpanded, setRecoveryExpanded] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoverySending, setRecoverySending] = useState(false);
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  useEffect(() => {
    if (isEditMode && event) {
      fetchExistingRegistration();
    }
  }, [isEditMode, event]);

  const fetchEvent = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventsService.getPublicEvent(id);
      setEvent(data);
      
      // Check if event is in the past
      if (dayjs(data.endDateTime).isBefore(dayjs())) {
        setError('pastEvent');
      }
    } catch (error) {
      if (error.status === 404) {
        setError('notFound');
      } else {
        setError('generic');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingRegistration = async () => {
    try {
      // We don't have a direct endpoint to get registration by code
      // The user will need to re-enter their email to validate
    } catch (error) {
      console.error('Error fetching registration:', error);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (isEditMode) {
        await eventsService.updateMyRegistration(id, {
          email: values.email,
          editCode: editCode,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone || null,
          remarks: values.remarks || null,
        });
        message.success(t('events.registration.updateSuccess', 'Registration updated successfully'));
        setSubmitted(true);
        setRegistrationResult({ editCode });
      } else {
        const result = await eventsService.registerForEvent(id, {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone || null,
          remarks: values.remarks || null,
        }, { suppressNotification: true });
        setSubmitted(true);
        setRegistrationResult(result);
      }
    } catch (error) {
      if (error.status === 409) {
        message.error(t('events.registration.alreadyRegistered', 'This email is already registered for this event'));
      } else if (error.status === 401) {
        message.error(t('events.registration.invalidCode', 'Invalid edit code or email'));
      } else {
        message.error(t('events.registration.error', 'Registration failed. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    const email = form.getFieldValue('email');
    if (!email) {
      message.error(t('events.registration.enterEmail', 'Please enter your email to cancel'));
      setCancelModalVisible(false);
      return;
    }

    try {
      await eventsService.cancelMyRegistration(id, {
        email,
        editCode,
      });
      message.success(t('events.registration.cancelSuccess', 'Registration cancelled successfully'));
      navigate(`/events/${id}`);
    } catch (error) {
      message.error(t('events.registration.cancelError', 'Failed to cancel registration'));
    } finally {
      setCancelModalVisible(false);
    }
  };

  const copyEditCode = () => {
    if (registrationResult?.editCode) {
      navigator.clipboard.writeText(registrationResult.editCode);
      message.success(t('events.registration.codeCopied', 'Edit code copied to clipboard'));
    }
  };

  const handleRecoveryRequest = async () => {
    if (!recoveryEmail || !recoveryEmail.includes('@')) {
      message.error(t('events.registration.emailInvalid', 'Please enter a valid email'));
      return;
    }

    setRecoverySending(true);
    try {
      await eventsService.requestEditCodeRecovery(id, recoveryEmail);
      setRecoverySuccess(true);
      message.success(t('events.registration.recoverySuccess', 'If you are registered, you will receive an email shortly'));
    } catch (error) {
      if (error.status === 429) {
        message.error(t('events.registration.recoveryRateLimit', 'You can only request one recovery email per day. Please try again tomorrow.'));
      } else {
        message.error(t('events.registration.recoveryError', 'Failed to send recovery email. Please try again.'));
      }
    } finally {
      setRecoverySending(false);
    }
  };

  const formatEventDate = (startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    if (start.isSame(end, 'day')) {
      return `${start.format('dddd, MMMM D, YYYY')} • ${start.format('HH:mm')} - ${end.format('HH:mm')}`;
    }
    return `${start.format('DD/MM/YYYY HH:mm')} - ${end.format('DD/MM/YYYY HH:mm')}`;
  };

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  if (error) {
    return (
      <PagesWrapper>
        <Result
          status={error === 'notFound' ? '404' : error === 'pastEvent' ? 'info' : 'error'}
          title={
            error === 'notFound' 
              ? t('events.notFound', 'Event Not Found')
              : error === 'pastEvent'
              ? t('events.pastEvent', 'Event Has Ended')
              : t('events.loadError', 'Failed to Load Event')
          }
          subTitle={
            error === 'notFound'
              ? t('events.notFoundDesc', 'This event may have been removed or is no longer available.')
              : error === 'pastEvent'
              ? t('events.pastEventDesc', 'Registration is closed for events that have already taken place.')
              : t('events.loadErrorDesc', 'Something went wrong. Please try again later.')
          }
          extra={[
            <Button type="primary" key="back" onClick={() => navigate('/events')}>
              {t('events.backToEvents', 'Back to Events')}
            </Button>,
          ]}
        />
      </PagesWrapper>
    );
  }

  // Success state
  if (submitted && registrationResult) {
    return (
      <PagesWrapper>
        <div className={styles.container}>
          <Card className={styles.successCard}>
            <Result
              status="success"
              icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              title={isEditMode 
                ? t('events.registration.updateSuccessTitle', 'Registration Updated!')
                : t('events.registration.successTitle', 'Registration Successful!')
              }
              subTitle={isEditMode
                ? t('events.registration.updateSuccessDesc', 'Your registration has been updated.')
                : t('events.registration.successDesc', 'You are now registered for this event.')
              }
            />

            {!isEditMode && registrationResult.editCode && (
              <div className={styles.editCodeSection}>
                <Alert
                  type="info"
                  showIcon
                  message={t('events.registration.confirmationEmailTitle', 'Confirmation email sent')}
                  description={t(
                    'events.registration.confirmationEmailDesc',
                    'Check your inbox for your registration confirmation. It includes your edit code and an .ics calendar file you can add when you want.'
                  )}
                  style={{ marginBottom: 16 }}
                />
                <Alert
                  type="warning"
                  showIcon
                  message={t('events.registration.importantSaveCode', 'Important: Save Your Edit Code')}
                  description={
                    <div className={styles.editCodeAlert}>
                      <Paragraph>
                        <strong>{t('events.registration.emailBackupNotice', 'Keep this code as a backup.')}</strong>
                        {' '}
                        {t('events.registration.codeDesc', 'Use this code to modify or cancel your registration later:')}
                      </Paragraph>
                      <div className={styles.editCodeDisplay}>
                        <Text code className={styles.editCode}>{registrationResult.editCode}</Text>
                        <Button 
                          icon={<CopyOutlined />} 
                          onClick={copyEditCode}
                          size="small"
                        >
                          {t('common.copy', 'Copy')}
                        </Button>
                      </div>
                      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                        {t('events.registration.recoveryNote', 'Lost your code? You can request a recovery email (once per day) from the event page.')}
                      </Paragraph>
                    </div>
                  }
                />
              </div>
            )}

            <div className={styles.successActions}>
              <Space>
                <Button type="primary" onClick={() => navigate(`/events/${id}`)}>
                  {t('events.viewEvent', 'View Event Details')}
                </Button>
                <Button onClick={() => navigate('/events')}>
                  {t('events.browseEvents', 'Browse More Events')}
                </Button>
              </Space>
            </div>
          </Card>
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.container}>
        {/* Back Link */}
        <Link to={`/events/${id}`} className={styles.backLink}>
          <ArrowLeftOutlined /> {t('events.backToEvent', 'Back to Event')}
        </Link>

        <div className={styles.contentGrid}>
          {/* Registration Form */}
          <Card className={styles.formCard}>
            <Title level={3} className={styles.formTitle}>
              {isEditMode 
                ? t('events.registration.editTitle', 'Edit Registration')
                : t('events.registration.title', 'Register for Event')
              }
            </Title>

            {/* Lost your code? Section - only show when not in edit mode */}
            {!isEditMode && (
              <div className={styles.recoverySection}>
                <div 
                  className={styles.recoveryHeader}
                  onClick={() => setRecoveryExpanded(!recoveryExpanded)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setRecoveryExpanded(!recoveryExpanded)}
                >
                  <QuestionCircleOutlined className={styles.recoveryIcon} />
                  <Text>{t('events.registration.lostCode', 'Already registered? Lost your edit code?')}</Text>
                </div>
                {recoveryExpanded && (
                  <div className={styles.recoveryContent}>
                    {recoverySuccess ? (
                      <Alert
                        type="success"
                        message={t('events.registration.recoveryEmailSent', 'Recovery email sent!')}
                        description={t('events.registration.recoveryEmailSentDesc', 'If your email is registered for this event, you will receive your edit code shortly. Check your inbox (and spam folder).')}
                        showIcon
                      />
                    ) : (
                      <>
                        <Paragraph type="secondary">
                          {t('events.registration.recoveryDesc', 'Enter your registered email address and we\'ll send you your edit code. You can request one recovery email per day.')}
                        </Paragraph>
                        <div className={styles.recoveryInputRow}>
                          <Input
                            prefix={<MailOutlined />}
                            placeholder={t('events.registration.emailPlaceholder', 'your.email@example.com')}
                            value={recoveryEmail}
                            onChange={(e) => setRecoveryEmail(e.target.value)}
                            onPressEnter={handleRecoveryRequest}
                            disabled={recoverySending}
                          />
                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={handleRecoveryRequest}
                            loading={recoverySending}
                          >
                            {t('events.registration.sendRecovery', 'Send')}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <Divider />
              </div>
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className={styles.form}
            >
              <Form.Item
                name="email"
                label={t('events.registration.email', 'Email Address')}
                rules={[
                  { required: true, message: t('events.registration.emailRequired', 'Email is required') },
                  { type: 'email', message: t('events.registration.emailInvalid', 'Please enter a valid email') },
                ]}
              >
                <Input 
                  prefix={<MailOutlined />}
                  placeholder={t('events.registration.emailPlaceholder', 'your.email@example.com')}
                  size="large"
                />
              </Form.Item>

              <div className={styles.nameRow}>
                <Form.Item
                  name="firstName"
                  label={t('events.registration.firstName', 'First Name')}
                  rules={[{ required: true, message: t('events.registration.firstNameRequired', 'First name is required') }]}
                >
                  <Input 
                    prefix={<UserOutlined />}
                    placeholder={t('events.registration.firstNamePlaceholder', 'John')}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="lastName"
                  label={t('events.registration.lastName', 'Last Name')}
                  rules={[{ required: true, message: t('events.registration.lastNameRequired', 'Last name is required') }]}
                >
                  <Input 
                    placeholder={t('events.registration.lastNamePlaceholder', 'Doe')}
                    size="large"
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="phone"
                label={t('events.registration.phone', 'Phone Number')}
                extra={t('events.registration.phoneOptional', 'Optional')}
              >
                <Input 
                  prefix={<PhoneOutlined />}
                  placeholder={t('events.registration.phonePlaceholder', '+1 234 567 890')}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="remarks"
                label={t('events.registration.remarks', 'Remarks')}
                extra={t('events.registration.remarksOptional', 'Optional - special requirements, dietary restrictions, etc.')}
              >
                <TextArea 
                  rows={3}
                  placeholder={t('events.registration.remarksPlaceholder', 'Any special requirements or notes...')}
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item className={styles.submitItem}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  size="large"
                  loading={submitting}
                  block
                >
                  {isEditMode 
                    ? t('events.registration.update', 'Update Registration')
                    : t('events.registration.register', 'Complete Registration')
                  }
                </Button>
              </Form.Item>

              {isEditMode && (
                <div className={styles.cancelSection}>
                  <Button 
                    danger 
                    icon={<DeleteOutlined />}
                    onClick={() => setCancelModalVisible(true)}
                  >
                    {t('events.registration.cancel', 'Cancel Registration')}
                  </Button>
                </div>
              )}
            </Form>
          </Card>

          {/* Event Summary */}
          <Card className={styles.summaryCard}>
            <Title level={4} className={styles.summaryTitle}>
              {t('events.registration.eventSummary', 'Event Summary')}
            </Title>

            {event.bannerImageUrl && (
              <img 
                src={getFileUrl(event.bannerImageUrl)} 
                alt={event.title}
                className={styles.summaryImage}
              />
            )}

            <Title level={5} className={styles.eventName}>
              {event.title}
            </Title>

            <div className={styles.summaryDetails}>
              <div className={styles.summaryItem}>
                <CalendarOutlined className={styles.summaryIcon} />
                <Text>{formatEventDate(event.startDateTime, event.endDateTime)}</Text>
              </div>
              <div className={styles.summaryItem}>
                <EnvironmentOutlined className={styles.summaryIcon} />
                <Text>{event.location}</Text>
              </div>
            </div>

            {event.purpose && (
              <>
                <Divider />
                <Paragraph type="secondary" className={styles.summaryPurpose}>
                  {event.purpose}
                </Paragraph>
              </>
            )}
          </Card>
        </div>

        {/* Cancel Modal */}
        <Modal
          title={t('events.registration.cancelTitle', 'Cancel Registration')}
          open={cancelModalVisible}
          onOk={handleCancel}
          onCancel={() => setCancelModalVisible(false)}
          okText={t('events.registration.confirmCancel', 'Yes, Cancel')}
          okButtonProps={{ danger: true }}
          cancelText={t('common.no', 'No')}
        >
          <Paragraph>
            {t('events.registration.cancelConfirm', 'Are you sure you want to cancel your registration for this event?')}
          </Paragraph>
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default EventRegistration;
